import React from 'react'
import { Box, FormControlLabel, Switch, Typography, alpha } from '@mui/material'

export type ToggleActiveColor = 'success' | 'warning' | 'error' | 'info' | 'primary'

export interface ToggleFieldProps {
  label: React.ReactNode
  checked: boolean
  onChange: (checked: boolean) => void
  /** Optional helper line under the label. */
  description?: React.ReactNode
  /**
   * Palette colour applied while ON - tints the switch, the row border and a
   * faint fill so an active flag reads at a glance. Omit for a plain toggle.
   */
  activeColor?: ToggleActiveColor
  disabled?: boolean
}

/**
 * A bordered label + switch row for boolean form flags (e.g. ICU, Selling).
 * The whole row is clickable (built on `FormControlLabel`), not just the switch.
 * With `activeColor` the row lights up in that palette colour when on. For a
 * switch that reveals a whole block, use `ToggleGatedSection` instead.
 */
const ToggleField: React.FC<ToggleFieldProps> = ({
  label,
  checked,
  onChange,
  description,
  activeColor,
  disabled = false,
}) => {
  const active = checked && Boolean(activeColor)
  return (
    <FormControlLabel
      checked={checked}
      onChange={(_event, value) => onChange(value)}
      disabled={disabled}
      labelPlacement="start"
      control={<Switch color={activeColor ?? 'primary'} size="small" />}
      label={
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {label}
          </Typography>
          {description ? (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              {description}
            </Typography>
          ) : null}
        </Box>
      }
      sx={{
        m: 0,
        width: '100%',
        justifyContent: 'space-between',
        px: 1.5,
        py: 0.75,
        borderRadius: 1,
        border: '1px solid',
        borderColor: active ? `${activeColor}.main` : 'divider',
        bgcolor: (theme) =>
          active ? alpha(theme.palette[activeColor as ToggleActiveColor].main, 0.08) : 'transparent',
        transition: 'border-color .15s, background-color .15s',
        '& .MuiFormControlLabel-label': { flex: 1, minWidth: 0 },
      }}
    />
  )
}

export default ToggleField
